<template>
  <v-container>
    <modal-dialog
      title="Выберите дату и время"
      v-bind:value="dialog"
      v-on:on-click-outside="switchModal"
    >
      <v-date-picker
        class="employee-work-time"
        first-day-of-week="1"
        locale="ru-ru"
        no-title
        v-model="date"
        v-on:change="updateDateTime"
      >
        <div class="employee-work-time__input">
          <input-number
            title="Часы"
            v-bind:min="minHours"
            v-bind:max="maxHours"
            v-model="hours"
            v-on:input="onInputHours"
          />
          <input-number
            title="Минуты"
            v-bind:min="minMinutes"
            v-bind:max="maxMinutes"
            v-model="minutes"
            v-on:input="onInputMinutes"
          />
        </div>
        <div class="employee-work-time__wrapper-button">
          <button
            class="button button--cancel"
            type="button"
            v-on:click="dialog = false">
            Отмена
          </button>
          <button
            class="button button--submit"
            type="button"
            v-on:click="onSubmit">
            {{ buttonTitle }}
          </button>
        </div>
      </v-date-picker>
    </modal-dialog>
    <the-select
      v-if="isAssistant && doctorsToAssist.length > 0"
      class="employee-work-time-controls__doctor"
      title="Врач"
      placeholder="Выберите врача"
      option-title-key="name"
      option-value-key="id"
      v-bind:value="doctorAssist"
      v-bind:options="doctorsToAssist"
      v-bind:disabled="nextAction === 'stop'"
      v-on:input="onInputDoctorAssist"
    />
    <div class="time-button">
      <span class="time-button__text">Дата и время</span>
      <button
        class="time-button__button"
        type="button"
        v-on:click="onOpenDialog">
        {{ dateTimeTitle }}
      </button>
    </div>
    <button
      v-bind:class="['button-player', `button-player--${nextAction}`]"
      type="button"
      v-on:click="setCurrentDateTime(); onSubmit()"
    />
  </v-container>
</template>

<script>
import InputNumber from '@/components/InputNumber.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import TheSelect from '@/components/TheSelect.vue';

/**
 * @description Счетчик времени.
 * @type {Vue}
 */
export default {
  components: {
    ModalDialog,
    InputNumber,
    TheSelect,
  },
  computed: {
    buttonTitle() {
      return this.nextAction === 'start' ? 'Старт' : 'Стоп';
    },
  },
  created() {
    this.setCurrentDateTime();
  },
  data() {
    return {
      dialog: false,
      date: null,
      minHours: '00',
      maxHours: '23',
      minMinutes: '00',
      maxMinutes: '59',
      dateISO: '',
      hours: '00',
      minutes: '00',
      seconds: '00',
      dateTimeTitle: '',
      timeZone: '0',
    };
  },
  methods: {
    /**
     * Переключить состояние модального окна
     * @return void
     */
    switchModal() {
      this.dialog = !this.dialog;
    },
    /**
     * @description Установить текущие дату и время.
     * @returns {void}
     */
    setCurrentDateTime() {
      const now = new Date();
      this.date = now.toISOString().substr(0, 10);
      this.hours = this.padZero(now.getHours());
      this.minutes = this.padZero(now.getMinutes());
      this.seconds = this.padZero(now.getSeconds());
      this.timeZone = -new Date().getTimezoneOffset() / 60;
      this.updateDateTime();
    },
    /**
     * @description Получить формат даты dd-mm-YY
     *
     * @return {string}
     */
    formatDateToDDMMYY() {
      const date = new Date(this.date);
      const day = this.padZero(date.getDate());
      const month = this.padZero(date.getMonth() + 1);
      const year = date.getFullYear().toString().slice(2);
      return `${day}.${month}.${year}`;
    },
    /**
     * @description Добавить ноль в начало.
     * @param value
     * @returns {string|*}
     */
    padZero(value) {
      return Number(value) < 10 && String(value).length < 2 ? `0${value}` : value;
    },
    /**
     * @description Обновить дату и время.
     * @returns {void}
     */
    updateDateTime() {
      const minutes = this.padZero(this.minutes);
      const hours = this.padZero(this.hours);
      const date = this.formatDateToDDMMYY();
      this.dateTimeTitle = `${date} ${hours}:${minutes}`;
      this.dateISO = `${this.date}T${hours}:${minutes}:${this.seconds}${this.timeZone > 0 ? '+' : '-'}${this.padZero(Math.abs(this.timeZone))}:00`;
    },
    /**
     * @description Обработать ввода часов.
     * @returns {void}
     */
    onInputHours() {
      this.updateDateTime();
    },
    /**
     * @description Обработать ввод минут.
     * @returns {void}
     */
    onInputMinutes() {
      this.updateDateTime();
    },
    /**
     * @description Обработать открытие диалогового окна.
     * @returns {void}
     */
    onOpenDialog() {
      this.dialog = true;
      this.setCurrentDateTime();
    },
    /**
     * @description Обработать старт/стоп таймера.
     * @returns {void}
     */
    onSubmit() {
      if (this.nextAction === 'start') {
        this.$emit('start', this.dateISO);
      } else {
        this.$emit('stop', this.dateISO);
      }
      this.dialog = false;
    },
    /**
     * @description Обработать выбор доктора для ассистирования.
     * @returns {void}
     */
    onInputDoctorAssist(doctorId) {
      this.$emit('select-doctor', doctorId);
    },
  },
  name: 'WorkTimeCounter',
  props: {
    /**
     * @description Следующее действие.
     * @type {String}
     */
    nextAction: { type: String, required: true },
    isAssistant: { type: Boolean, required: true },
    doctorsToAssist: { type: Array, required: true },
    doctorAssist: { type: String || Number, required: true },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
@use "@/scss/fields";
.headline {
  font-weight: bold;
}
.time-button {
  display: flex;
  flex-direction: column;
  width: 100%;
  &__text {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 2px 0;
    color: var(--unfocus-color);
  }
  &__button {
    @include fields.input-field();
    text-align: left;
    cursor: text;
  }
}
.employee-work-time-controls__doctor + .time-button {
  grid-row: 2/3;
}
.employee-work-time {
  .v-card__actions {
    display: grid;
    gap: 15px;
  }
  &__input,
  &__wrapper-button {
    display: flex;
    gap: 15px;
  }
  &__input {
    .number {
      width: 100%;
    }
  }
  .button {
    &--submit {
      @include buttons.btn-blue(100%, 46px, 1rem);
      max-width: 150px;
    }
    &--cancel {
      @include buttons.btn-transparent(100%, 46px, 1rem);
      max-width: 150px;
    }
  }
}
.button-player {
  justify-self: center;
  grid-column: 2/3;
  grid-row: span 2;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  transition: background-color 0.2s, background-image 0.2s;
  &--start {
    background: url("@/assets/img/player/start.svg") no-repeat 50%;
    background-color: transparent;
    background-size: 29px;
    border: 2px solid #0489e0;
  }
  &--stop {
    background: url("@/assets/img/player/stop.svg") no-repeat 50%;
    background-color: #0489e0;
    background-size: 22px;
    border: none;
    outline: 5px solid rgba(4, 137, 224, 0.2);
  }
}

</style>
